import { TenantFeatureToggle, UserRole } from '@xeetr/types/enums'
import { formatInTimeZone } from 'date-fns-tz'
import { useAtomValue } from 'jotai'
import { Calendar, ClipboardList, LayoutDashboard, Settings, Users, BookOpen } from 'lucide-react'
import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import { bookingQueryParamsUrlAtom, tenantTimezoneAtom } from '../../../atoms/bookings'
import { currentMonthAtom } from '../../../atoms/calendar'
import useFeatureToggle from '../../../hooks/common/useFeatureToggle'
import useUserRole from '../../../hooks/common/useUserRole'
import { useTenant } from '../../../hooks/useTenant'
import { useUser } from '../../../hooks/useUser'

import UserInfo from './UserInfo'

import { Progress } from '@/components/ui/progress'
import { cn } from '@/lib/utils'

const Header = () => {
  const navigate = useNavigate()
  const bookingParams = useAtomValue(bookingQueryParamsUrlAtom)
  const currentMonth = useAtomValue(currentMonthAtom)
  const tenantTimezone = useAtomValue(tenantTimezoneAtom)

  const isActiveClass = (path: string) => {
    const activeClass = 'bg-primary/75 !hover:text-white/80'
    return cn({
      [activeClass]: location.pathname.startsWith(path),
    })
  }

  const TenantFeatureToggleCalendar = useFeatureToggle(TenantFeatureToggle.CALENDAR)
  const TenantFeatureToggleMonthlyOverview = useFeatureToggle(TenantFeatureToggle.MONTHLY_OVERVIEW)
  const TenantFeatureToggleCustomer = useFeatureToggle(TenantFeatureToggle.CUSTOMER_PAGE)
  const adminRole = useUserRole(UserRole.ADMIN)

  const tenant = useTenant()
  const { isLoading: userLoading } = useUser()
  const loading = useMemo(() => tenant.isLoading || userLoading, [tenant.isLoading, userLoading])

  const items = useMemo(() => {
    if (tenant.isLoading) return []
    return [
      {
        className: isActiveClass('/dashboard'),
        command: () => {
          const url = new URL('/dashboard', window.location.origin)
          if (bookingParams.state && bookingParams.state !== 'all') {
            url.searchParams.set('state', bookingParams.state)
          }
          if (bookingParams.from) {
            url.searchParams.set(
              'from',
              formatInTimeZone(bookingParams.from, tenantTimezone, 'yyyy-MM-dd HH:mm:ss')
            )
          }
          if (bookingParams.until) {
            url.searchParams.set(
              'until',
              formatInTimeZone(bookingParams.until, tenantTimezone, 'yyyy-MM-dd HH:mm:ss')
            )
          }
          navigate(url.pathname + url.search, { viewTransition: true })
        },
        label: 'Dashboard',
        icon: LayoutDashboard,
      },
      {
        className: isActiveClass('/requests'),
        command: () => {
          navigate('/requests', { viewTransition: true })
        },
        label: 'Anfragen',
        icon: BookOpen,
      },
      {
        className: isActiveClass('/calendar'),
        command: () => {
          navigate('/calendar', { viewTransition: true })
        },
        label: 'Kalender',
        icon: Calendar,
        visible: TenantFeatureToggleCalendar,
      },
      {
        className: isActiveClass('/monthly-overview'),
        command: () => {
          const url = new URL('/monthly-overview', window.location.origin)
          if (currentMonth) {
            url.searchParams.set('month', formatInTimeZone(currentMonth, tenantTimezone, 'yyyy-MM'))
          }
          navigate(url.pathname + url.search, { viewTransition: true })
        },
        label: 'Monatsübersicht',
        icon: ClipboardList,
        visible: TenantFeatureToggleMonthlyOverview,
      },
      {
        className: isActiveClass('/customers'),
        command: () => {
          navigate('/customers', { viewTransition: true })
        },
        label: 'Kunden',
        icon: Users,
        visible: TenantFeatureToggleCustomer,
      },
      {
        className: isActiveClass('/settings'),
        command: () => {
          navigate('/settings/user', { viewTransition: true })
        },
        label: 'Einstellungen',
        icon: Settings,
        visible: adminRole,
      },
    ]
  }, [tenant, location.pathname, adminRole])

  const start = (
    <img
      alt="xeetr logo"
      src="/xeetr-logo.png"
      className="mr-2 h-8 cursor-pointer lg:mr-0"
      onClick={() => navigate('/dashboard', { viewTransition: true })}
    />
  )
  const end = <UserInfo />

  return (
    <>
      <header className="flex-none bg-gray-900">
        <nav className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2 md:gap-6">
            {start}
            <div className="flex gap-1 lg:gap-2">
              {items
                .filter((item) => item.visible !== false)
                .map((item, index) => {
                  const Icon = item.icon
                  return (
                    <button
                      key={index}
                      onClick={item.command}
                      className={cn(
                        'hover:bg-primary/50 flex items-center gap-2 rounded px-3 py-2 text-sm font-medium text-white transition-colors lg:px-4 lg:py-2',
                        item.className
                      )}
                    >
                      <Icon className="h-4 w-4" />
                      <span className="hidden sm:inline">{item.label}</span>
                    </button>
                  )
                })}
            </div>
          </div>
          {end}
        </nav>
      </header>
      {loading && (
        <div className="h-2 w-full overflow-hidden">
          <Progress value={undefined} className="h-full animate-pulse" />
        </div>
      )}
    </>
  )
}

export default Header
